import React, { useEffect } from 'react';
import {
  appendExistingCsMktId,
  appendPlayStoreReferrer,
  trackPublicAttributionEvent,
} from '../services/marketingAttribution';
import { WEB_APP_URL } from '../services/api';

const APP_STORE_URL = 'https://apps.apple.com/app/checkstall-import/id6758943545';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.checkstall.client';
const WHATSAPP_URL = 'https://wa.me/2349067409148';

const getMobileStoreUrl = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isIOS = /iPad|iPhone|iPod/i.test(userAgent) || isTouchMac;
  const isAndroid = /Android/i.test(userAgent);
  const isMobile =
    isIOS ||
    isAndroid ||
    navigator.userAgentData?.mobile ||
    /webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  if (!isMobile) {
    return null;
  }

  if (isIOS) {
    return APP_STORE_URL;
  }

  if (isAndroid) {
    return appendPlayStoreReferrer(PLAY_STORE_URL);
  }

  return null;
};

const Downloads = () => {
  const handleContinueOnWeb = (event) => {
    event.currentTarget.href = appendExistingCsMktId(WEB_APP_URL);
  };

  const handlePlayStoreClick = (event) => {
    event.currentTarget.href = appendPlayStoreReferrer(PLAY_STORE_URL);
  };

  useEffect(() => {
    let cancelled = false;

    const redirectToStore = async () => {
      const storeUrl = getMobileStoreUrl();

      if (!storeUrl) {
        return;
      }

      await Promise.resolve();
      if (cancelled) return;
      await Promise.race([
        trackPublicAttributionEvent('download_redirect', {
          page: '/downloads', cta_location: 'automatic', trigger: 'automatic',
          store: storeUrl.startsWith(APP_STORE_URL) ? 'app_store' : 'google_play',
          destination: storeUrl.startsWith(APP_STORE_URL) ? 'app_store' : 'google_play',
        }),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);

      if (!cancelled) {
        window.location.replace(storeUrl);
      }
    };

    redirectToStore();

    return () => {
      cancelled = true;
    };
  }, []);

  const downloadLinks = [
    {
      platform: 'Google Play',
      href: PLAY_STORE_URL,
      label: 'Get it on Google Play',
      onClick: handlePlayStoreClick,
      type: 'android',
    },
    {
      platform: 'App Store',
      href: APP_STORE_URL,
      label: 'Download on the App Store',
      type: 'ios',
    },
  ];

  return (
    <main className="min-h-screen bg-white" data-cta-location="download_options">
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-gray-50">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
        <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-2xl text-center lg:mx-0 lg:text-left">
            <p className="mb-4 text-sm font-bold uppercase tracking-wide text-primary">
              Checkstall China Import
            </p>
            <h1 className="text-4xl font-extrabold leading-tight text-gray-950 sm:text-5xl lg:text-6xl">
              Import directly from China with Checkstall
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 sm:text-xl">
              Search products, make payments, track orders and manage your imports from your phone or browser.
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row lg:max-w-xl">
              {downloadLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.href}
                  onClick={link.onClick}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-14 flex-1 items-center justify-center rounded-2xl bg-gray-950 px-5 py-3 text-sm font-semibold text-white shadow-soft transition-colors duration-200 hover:bg-primary focus:outline-none focus:ring-4 focus:ring-primary-100"
                  aria-label={link.label}
                >
                  <span className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-950">
                    {link.type === 'android' ? (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85A1.5 1.5 0 0 1 3 20.5Zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27Zm3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31ZM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49Z" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83ZM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11Z" />
                      </svg>
                    )}
                  </span>
                  {link.label}
                </a>
              ))}
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex min-h-14 w-full items-center justify-center rounded-2xl border-2 border-green-600 bg-white px-8 py-4 text-base font-bold text-green-700 shadow-soft transition-colors duration-200 hover:bg-green-50 focus:outline-none focus:ring-4 focus:ring-green-100 sm:w-auto"
            >
              Chat with us on WhatsApp
            </a>

            <div className="my-8 flex items-center gap-4 lg:max-w-xl">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-sm font-semibold text-gray-500">or</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <a
              href={appendExistingCsMktId(WEB_APP_URL)}
              onClick={handleContinueOnWeb}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-14 w-full items-center justify-center rounded-2xl bg-primary px-8 py-4 text-base font-bold text-white shadow-soft-lg transition-colors duration-200 hover:bg-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-100 sm:w-auto"
            >
              Continue on Web
            </a>
            <p className="mt-4 text-sm text-gray-500">
              Already using Checkstall? Continue with the web app from any browser.
            </p>
          </div>

          <div className="mx-auto w-full max-w-lg lg:max-w-none">
            <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-soft-lg sm:p-6">
              <div className="rounded-2xl bg-gray-950 p-5 text-white sm:p-6">
                <div className="mb-8 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-300">Order tracking</p>
                    <p className="mt-1 text-2xl font-bold">CS Import Hub</p>
                  </div>
                  <div className="rounded-full bg-primary px-3 py-1 text-sm font-semibold">
                    Live
                  </div>
                </div>
                <div className="space-y-4">
                  {[
                    ['Product search', 'Find China suppliers'],
                    ['Secure payment', 'Pay from wallet or browser'],
                    ['Shipment updates', 'Track every package'],
                  ].map(([title, subtitle], index) => (
                    <div key={title} className="flex items-center gap-4 rounded-2xl bg-white/10 p-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold">{title}</p>
                        <p className="text-sm text-gray-300">{subtitle}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-primary-50 p-4">
                  <p className="text-sm text-gray-600">Products</p>
                  <p className="mt-2 text-2xl font-bold text-primary">Search</p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-sm text-gray-600">Imports</p>
                  <p className="mt-2 text-2xl font-bold text-gray-950">Manage</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Downloads;
